'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

// ─── COLOR PALETTE ────────────────────────────────────────────────────────────
const C = {
  gold: '#c9a84c',
  goldFaint: 'rgba(201,168,76,0.08)',
  goldBorder: 'rgba(201,168,76,0.2)',
  goldText: 'rgba(201,168,76,0.7)',
  cream: '#d4c5a9',
  creamFaint: 'rgba(212,197,169,0.55)',
  bg: '#060d12',
  bgCard: 'rgba(6,13,18,0.85)',
  green: '#2d7a47',
  teal: '#2d8a87',
  purple: '#7a4a8a',
  amber: '#a07840',
  creation: '#2d7a47',   // Creation Eye — forest green
  revelation: '#2d5a8a', // Revelation Eye — deep blue
  anfus: '#7a4a8a',      // Within — violet
  afaq: '#2d7a47',       // Around — green
  quranMode: '#2d5a8a',  // Revealed — blue
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. TWO-EYE BINOCULAR SYSTEM
// Creation Eye (IRL Lens) + Revelation Eye (Keys/Quran) running simultaneously
// Cross-pollination: Name extracted from creation surfaces in Revelation hemisphere
// ─────────────────────────────────────────────────────────────────────────────

const BINOCULAR_PAIRS = [
  {
    name: 'Al-Khaliq',
    arabic: 'الْخَالِق',
    creationAyah: {
      observation: 'Canopy Photosynthesis — the leaf converts light into sugar, fashioning chemical energy from radiant energy. The precise geometry of leaf arrangement is measured creation.',
      verse: 'Quran 6:99',
      text: 'It is He who sends down water from the sky; from it We bring forth the growth of everything.',
    },
    revelationAyah: {
      verse: 'Quran 2:29',
      text: 'He the One Who created for you what in the earth, all. Moreover, He turned to the heaven and fashioned them seven heavens. And He of every thing Al-Alim; The All-Knowing.',
      keys: 'Key 01 — Ayat: The Creator\'s origination is the first sign. Creation is not self-caused.',
    },
    depthPerception: 'The leaf seen through the Creation Eye alone is an ecological observation. The same leaf seen through both eyes simultaneously is an Ayah of Al-Khaliq confirmed by Quran 2:29. The depth perception that emerges is Ma\'rifah.',
  },
  {
    name: 'Ar-Razzaq',
    arabic: 'الرَّزَّاق',
    creationAyah: {
      observation: 'Mycorrhizal Network — fungal threads redistribute nutrients from trees with surplus to trees in deficit. Provision flows through the hidden network.',
      verse: 'Quran 15:20',
      text: 'And We have made for you in it means of living and whom you are not for him providers.',
    },
    revelationAyah: {
      verse: 'Quran 15:21',
      text: 'And not any thing but with Us its treasures, and We don\'t send down but in a known measure.',
      keys: 'Key 05 — Amanah: The provision is measured and entrusted. The MRG bridges people to what was already sent down.',
    },
    depthPerception: 'The mycorrhizal network seen through the Creation Eye is a nutrient redistribution system. Through both eyes: it is the Sunnat Allah of Ar-Razzaq — provision flowing through hidden channels, measured and purposeful.',
  },
  {
    name: 'An-Nur',
    arabic: 'النُّور',
    creationAyah: {
      observation: 'Light enters the forest canopy, filtered through layers of leaves. Each layer absorbs specific wavelengths. The light that reaches the forest floor is transformed provision.',
      verse: 'Quran 24:35',
      text: 'Allah is the Light of the heavens and the earth.',
    },
    revelationAyah: {
      verse: 'Quran 24:35',
      text: 'The parable of His Light is as if there were a Niche and within it a Lamp: the Lamp enclosed in Glass: the glass as it were a brilliant star.',
      keys: 'Key 02 — Haal: The state of illumination — Basar (outer sight) aligned with Basirah (inner sight) — is the fruit of An-Nur recognized in both creation and revelation.',
    },
    depthPerception: 'Light filtering through canopy seen through the Creation Eye is photosynthesis. Through both eyes: it is An-Nur — the same Light described in Quran 24:35 — made manifest in the forest. Next time you observe photosynthesis, look for this.',
  },
  {
    name: 'As-Sabur',
    arabic: 'الصَّبُور',
    creationAyah: {
      observation: 'Big Bluestem grass spends five years building a root system twice the depth of its visible height. The prairie burns. The bluestem returns. Patience is structural — encoded in the biology.',
      verse: 'Quran 87:4-5',
      text: 'And the One Who brings forth the pasture, then makes it stubble, dark.',
    },
    revelationAyah: {
      verse: 'Quran 2:153',
      text: 'O you who have believed, seek help through patience and prayer. Indeed, Allah is with the patient.',
      keys: 'Key 03 — Taqwah: The consciousness of Allah that sustains through difficulty. As-Sabur is not passive — it is active root-building beneath the surface.',
    },
    depthPerception: 'The bluestem\'s five-year grass stage seen through the Creation Eye is ecological patience. Through both eyes: it is As-Sabur — the same patience Quran 2:153 calls the believer to — made visible in the prairie. The root system is the Taqwah.',
  },
];

export function TwoEyeBinocularSystem() {
  const [activeNameIdx, setActiveNameIdx] = useState(0);
  const [eyeMode, setEyeMode] = useState<'creation' | 'revelation' | 'both'>('both');
  const [crossPollinated, setCrossPollinated] = useState<string[]>([]);

  const activePair = BINOCULAR_PAIRS[activeNameIdx];

  const handleCrossPollinate = (name: string) => {
    if (!crossPollinated.includes(name)) {
      setCrossPollinated(prev => [...prev, name]);
    }
  };

  return (
    <div style={{ margin: '40px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          ◈ TWO-EYE PRINCIPLE — BINOCULAR SPIRITUAL VISION
        </div>
        <h3 style={{ fontSize: 20, color: C.cream, fontWeight: 600, marginBottom: 8 }}>
          Creation Eye + Revelation Eye
        </h3>
        <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.75, maxWidth: 680, marginBottom: 16 }}>
          "The senses we're given allow us to <em>perceive</em> Ayat, Signs in creation. Our sense allows us to <em>receive</em> Ayat, Signs of Revelation." — Two types of Ayat. Same word. Different mode of reception. When both eyes are open simultaneously, the caretaker perceives in stereo. This is Ma'rifah.
        </p>

        {/* Eye mode selector */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(['creation', 'revelation', 'both'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setEyeMode(mode)}
              style={{
                background: eyeMode === mode
                  ? mode === 'creation' ? `${C.creation}20` : mode === 'revelation' ? `${C.revelation}20` : 'rgba(201,168,76,0.12)'
                  : 'transparent',
                border: `1px solid ${eyeMode === mode
                  ? mode === 'creation' ? C.creation : mode === 'revelation' ? C.revelation : C.gold :'rgba(201,168,76,0.15)'}`,
                color: eyeMode === mode
                  ? mode === 'creation' ? C.creation : mode === 'revelation' ? '#6a9fd8' : C.gold :'#7a6f5d',
                fontSize: 9,
                padding: '6px 14px',
                cursor: 'pointer',
                borderRadius: 4,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                transition: 'all 0.15s',
              }}
            >
              {mode === 'creation' ? '🌿 Creation Eye' : mode === 'revelation' ? '📖 Revelation Eye' : '◎ Both Eyes (Binocular)'}
            </button>
          ))}
        </div>
      </div>

      {/* Name selector */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {BINOCULAR_PAIRS.map((pair, i) => (
          <button
            key={pair.name}
            onClick={() => setActiveNameIdx(i)}
            style={{
              background: activeNameIdx === i ? 'rgba(201,168,76,0.1)' : 'transparent',
              border: `1px solid ${activeNameIdx === i ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.12)'}`,
              color: activeNameIdx === i ? C.gold : '#7a6f5d',
              fontSize: 10,
              padding: '5px 12px',
              cursor: 'pointer',
              borderRadius: 4,
              transition: 'all 0.15s',
            }}
          >
            {pair.name}
            {crossPollinated.includes(pair.name) && (
              <span style={{ marginLeft: 4, fontSize: 8, color: C.gold }}>✦</span>
            )}
          </button>
        ))}
      </div>

      {/* Binocular display */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: eyeMode === 'both' ? '1fr 1fr' : '1fr',
        gap: 16,
        marginBottom: 16,
      }}>
        {/* Creation Eye */}
        {(eyeMode === 'creation' || eyeMode === 'both') && (
          <div style={{
            background: `${C.creation}08`,
            border: `1px solid ${C.creation}30`,
            borderTop: `3px solid ${C.creation}`,
            borderRadius: 10,
            padding: '20px',
          }}>
            <div style={{ fontSize: 8, color: C.creation, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
              🌿 CREATION EYE — IRL LENS
            </div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: C.gold, marginBottom: 6, direction: 'rtl' }}>
              {activePair.arabic}
            </div>
            <div style={{ fontSize: 14, color: C.cream, fontWeight: 500, marginBottom: 12 }}>{activePair.name}</div>
            <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.75, fontStyle: 'italic', marginBottom: 12 }}>
              {activePair.creationAyah.observation}
            </p>
            <div style={{
              background: 'rgba(45,122,71,0.06)',
              border: '1px solid rgba(45,122,71,0.2)',
              borderRadius: 6,
              padding: '10px 12px',
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 9, color: C.creation, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                {activePair.creationAyah.verse}
              </div>
              <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.65, fontStyle: 'italic' }}>
                "{activePair.creationAyah.text}"
              </p>
            </div>
            <button
              onClick={() => handleCrossPollinate(activePair.name)}
              style={{
                background: 'none',
                border: `1px solid ${C.creation}40`,
                color: C.creation,
                fontSize: 9,
                padding: '5px 10px',
                cursor: 'pointer',
                borderRadius: 4,
                letterSpacing: '.08em',
              }}
            >
              ↔ Cross-pollinate to Revelation Eye
            </button>
          </div>
        )}

        {/* Revelation Eye */}
        {(eyeMode === 'revelation' || eyeMode === 'both') && (
          <div style={{
            background: `${C.revelation}08`,
            border: `1px solid ${C.revelation}30`,
            borderTop: `3px solid ${C.revelation}`,
            borderRadius: 10,
            padding: '20px',
          }}>
            <div style={{ fontSize: 8, color: '#6a9fd8', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
              📖 REVELATION EYE — KEYS CURRICULUM
            </div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: C.gold, marginBottom: 6, direction: 'rtl' }}>
              {activePair.arabic}
            </div>
            <div style={{ fontSize: 14, color: C.cream, fontWeight: 500, marginBottom: 12 }}>{activePair.name}</div>
            <div style={{
              background: 'rgba(45,90,138,0.08)',
              border: '1px solid rgba(45,90,138,0.25)',
              borderRadius: 6,
              padding: '10px 12px',
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 9, color: '#6a9fd8', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                {activePair.revelationAyah.verse}
              </div>
              <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.65, fontStyle: 'italic', marginBottom: 8 }}>
                "{activePair.revelationAyah.text}"
              </p>
              <p style={{ fontSize: 10, color: '#6a9fd8', lineHeight: 1.6 }}>
                {activePair.revelationAyah.keys}
              </p>
            </div>
            {crossPollinated.includes(activePair.name) && (
              <div style={{
                background: 'rgba(201,168,76,0.06)',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 10,
                color: C.gold,
              }}>
                ✦ Cross-pollinated from Creation Eye — what you observed in creation confirms this verse
              </div>
            )}
          </div>
        )}
      </div>

      {/* Depth perception — binocular vision result */}
      {eyeMode === 'both' && (
        <div style={{
          background: 'rgba(201,168,76,0.05)',
          border: '1px solid rgba(201,168,76,0.25)',
          borderLeft: '3px solid rgba(201,168,76,0.6)',
          borderRadius: '0 8px 8px 0',
          padding: '16px 20px',
        }}>
          <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            ◎ DEPTH PERCEPTION — MA'RIFAH
          </div>
          <p style={{ fontSize: 13, color: C.cream, lineHeight: 1.8, fontStyle: 'italic' }}>
            {activePair.depthPerception}
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. THREE-MODE OBSERVATION PANEL
// Anfus (Within) / Afaq (Around) / Quran (Revealed)
// Same 8-layer pathway, different prompts — accessible to imprisoned caretakers
// ─────────────────────────────────────────────────────────────────────────────

const THREE_MODE_LAYERS = [
  { layer: 1, name: 'Encounter / Zahir', anfus: 'What am I feeling right now? Name the sensation in the body.', afaq: 'What do I see, hear, smell, touch in this space?', quran: 'What verse has arrived to me? Read it slowly once.' },
  { layer: 2, name: 'Three Questions', anfus: 'Is this state real? What is its nature? When did it begin?', afaq: 'Does this thing exist? What is it? When did it appear?', quran: 'Does this verse address my state? What is its nature? When was it revealed?' },
  { layer: 3, name: 'Names Extraction', anfus: 'Which Names of Allah are active in this inner state? (Al-Qabid if constricted, Al-Basit if expansive...)', afaq: 'Which Names of Allah are visible in this thing? Extract 2-4 Names.', quran: 'Which Names of Allah appear in this verse? List them.' },
  { layer: 4, name: 'Sunnat Allah', anfus: 'What pattern of Allah\'s Way is operating in this state? Has this state followed a recognizable rhythm?', afaq: 'What divine process is operating here? What is the Sunnat Allah in this system?', quran: 'What divine process does this verse describe? What Sunnat Allah does it name?' },
  { layer: 5, name: 'Zahir → Batin', anfus: 'What is visible in this state (Zahir)? What is hidden beneath it (Batin)?', afaq: 'What is visible in this thing (Zahir)? What is hidden beneath it (Batin)?', quran: 'What is the apparent meaning (Zahir)? What deeper meaning (Batin) does this verse carry?' },
  { layer: 6, name: 'Haqq', anfus: 'This state is an Ayah declaring that... (complete the sentence)', afaq: 'This encounter is an Ayah declaring that... (complete the sentence)', quran: 'This verse is telling me that... (in my specific context, right now)' },
  { layer: 7, name: 'Amanah', anfus: 'This recognition calls me to... (inner action: Muhasaba, Tawbah, Shukr...)', afaq: 'This recognition calls me to... (outer action: bridge, serve, document...)', quran: 'This verse calls me to... (what action does it demand of me today?)' },
  { layer: 8, name: 'Uboor / Crossing', anfus: 'Alhamdulillah. What has shifted? What do I carry forward from this inner encounter?', afaq: 'Alhamdulillah. What do I carry forward from this encounter with creation?', quran: 'Alhamdulillah. What do I carry forward from this encounter with Revelation?' },
];

export function ThreeModeObservationPanel() {
  const [mode, setMode] = useState<'anfus' | 'afaq' | 'quran'>('afaq');
  const [activeLayer, setActiveLayer] = useState<number | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});

  const modeConfig = {
    anfus: { label: 'Anfus — Within', arabic: 'أَنْفُس', color: C.anfus, icon: '◉', desc: 'Observe your inner states. For caretakers in any circumstance — your cell is your station, your inner state is your biome.' },
    afaq: { label: 'Afaq — Around', arabic: 'آفَاق', color: C.afaq, icon: '◎', desc: 'Observe creation around you. Walk your ward, observe the ecology, document what you see.' },
    quran: { label: 'Quran — Revealed', arabic: 'قُرْآن', color: C.quranMode, icon: '◈', desc: 'Observe a verse of Revelation. The same 8-layer pathway applied to the Quran.' },
  };

  const cfg = modeConfig[mode];

  const handleResponse = (layerIdx: number, value: string) => {
    setResponses(prev => ({ ...prev, [`${mode}-${layerIdx}`]: value }));
  };

  return (
    <div style={{ margin: '40px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          ◈ THREE-MODE OBSERVATION PANEL — ANFUS / AFAQ / QURAN
        </div>
        <h3 style={{ fontSize: 20, color: C.cream, fontWeight: 600, marginBottom: 8 }}>
          Three Locations of Ayat
        </h3>
        <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.75, maxWidth: 680, marginBottom: 16 }}>
          "Some of the Ayat, Signs are inherent within us, some are in nature around us, and most critically, some are Revealed to us." — Three observation modes. Same 8-layer pathway. Different prompts. The imprisoned caretaker can observe in Anfus Mode and Quran Mode. Their cell is their station.
        </p>

        {/* Mode selector */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(Object.entries(modeConfig) as [typeof mode, typeof modeConfig[typeof mode]][]).map(([key, val]) => (
            <button
              key={key}
              onClick={() => { setMode(key); setActiveLayer(null); }}
              style={{
                background: mode === key ? `${val.color}15` : 'transparent',
                border: `1px solid ${mode === key ? val.color : 'rgba(201,168,76,0.15)'}`,
                color: mode === key ? val.color : '#7a6f5d',
                fontSize: 10,
                padding: '8px 16px',
                cursor: 'pointer',
                borderRadius: 6,
                letterSpacing: '.08em',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ marginRight: 6 }}>{val.icon}</span>
              {val.label}
              <span style={{ marginLeft: 6, fontFamily: "'Amiri', serif", fontSize: 12, opacity: 0.7 }}>{val.arabic}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mode description */}
      <div style={{
        background: `${cfg.color}08`,
        border: `1px solid ${cfg.color}25`,
        borderLeft: `3px solid ${cfg.color}`,
        borderRadius: '0 8px 8px 0',
        padding: '12px 16px',
        marginBottom: 20,
      }}>
        <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.7 }}>{cfg.desc}</p>
      </div>

      {/* 8-layer pathway */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {THREE_MODE_LAYERS.map((layer, i) => {
          const prompt = layer[mode];
          const responseKey = `${mode}-${i}`;
          const hasResponse = responses[responseKey]?.length > 0;
          const isActive = activeLayer === i;

          return (
            <div
              key={i}
              style={{
                background: isActive ? `${cfg.color}08` : 'rgba(6,13,18,0.5)',
                border: `1px solid ${isActive ? cfg.color + '40' : 'rgba(201,168,76,0.1)'}`,
                borderRadius: 8,
                overflow: 'hidden',
                transition: 'all 0.2s',
              }}
            >
              <button
                onClick={() => setActiveLayer(isActive ? null : i)}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  textAlign: 'left',
                }}
              >
                <span style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: hasResponse ? `${cfg.color}30` : 'rgba(201,168,76,0.06)',
                  border: `1px solid ${hasResponse ? cfg.color : 'rgba(201,168,76,0.2)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  color: hasResponse ? cfg.color : '#7a6f5d',
                  flexShrink: 0,
                }}>
                  {hasResponse ? '✓' : layer.layer}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, color: cfg.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>
                    Layer {layer.layer} — {layer.name}
                  </div>
                  <div style={{ fontSize: 11, color: C.creamFaint, lineHeight: 1.5 }}>{prompt}</div>
                </div>
                <span style={{ color: C.gold, fontSize: 12, flexShrink: 0 }}>{isActive ? '−' : '+'}</span>
              </button>

              {isActive && (
                <div style={{ padding: '0 16px 16px' }}>
                  <textarea
                    value={responses[responseKey] || ''}
                    onChange={e => handleResponse(i, e.target.value)}
                    placeholder={`${mode === 'anfus' ? 'Inner observation...' : mode === 'afaq' ? 'Creation observation...' : 'Quranic reflection...'}`}
                    style={{
                      width: '100%',
                      minHeight: 80,
                      background: 'rgba(6,13,18,0.6)',
                      border: `1px solid ${cfg.color}20`,
                      borderRadius: 6,
                      color: C.cream,
                      fontSize: 12,
                      lineHeight: 1.7,
                      padding: '10px 12px',
                      resize: 'vertical',
                      fontFamily: "'Georgia', serif",
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Completion note */}
      <div style={{ marginTop: 16, fontSize: 10, color: '#4a4035', fontStyle: 'italic', textAlign: 'center' }}>
        All three modes lead to the same Alhamdulillah. The station is wherever you are.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. FITRA DIAGNOSTIC
// Surfaces what the caretaker already recognizes intuitively
// Replaces lineage selection with lineage recognition
// ─────────────────────────────────────────────────────────────────────────────

const FITRA_RECOGNITIONS = [
  {
    id: 'provision',
    prompt: 'When you see food being prepared or shared, what do you feel?',
    options: [
      { text: 'Gratitude — someone provided this', lineage: 'water-guardian', name: 'Al-Latif / Ar-Razzaq' },
      { text: 'Concern — not everyone has this', lineage: 'community-caretaker', name: 'Al-Adl / Al-Muqsit' },
      { text: 'Wonder — how did this come to be?', lineage: 'forest-keeper', name: 'Al-Khaliq / Al-Musawwir' },
      { text: 'Responsibility — I should share this', lineage: 'returning-citizen', name: 'At-Tawwab / Al-Karim' },
    ],
  },
  {
    id: 'structure',
    prompt: 'When you see a well-designed system (a bridge, a forest, a community network), what do you notice first?',
    options: [
      { text: 'The elegance of the design', lineage: 'forest-keeper', name: 'Al-Hakim / Al-Musawwir' },
      { text: 'Who it serves and who it excludes', lineage: 'community-caretaker', name: 'Al-Adl / Al-Hafiz' },
      { text: 'The flow of resources through it', lineage: 'water-guardian', name: 'Al-Latif / Al-Muhyi' },
      { text: 'What it took to build it', lineage: 'returning-citizen', name: 'As-Sabur / Al-Qadir' },
    ],
  },
  {
    id: 'difficulty',
    prompt: 'When you encounter difficulty or injustice, your first impulse is:',
    options: [
      { text: 'To understand the root cause', lineage: 'forest-keeper', name: 'Al-Khabir / Al-Alim' },
      { text: 'To find a practical solution', lineage: 'community-caretaker', name: 'Al-Qadir / Al-Wakil' },
      { text: 'To find others who can help', lineage: 'water-guardian', name: 'Al-Jami / Al-Wadud' },
      { text: 'To restore what was broken', lineage: 'returning-citizen', name: 'Al-Jabbar / At-Tawwab' },
    ],
  },
  {
    id: 'creation',
    prompt: 'In nature, what draws your attention most naturally?',
    options: [
      { text: 'The hidden systems beneath the surface', lineage: 'forest-keeper', name: 'Al-Batin / Al-Khabir' },
      { text: 'The flow of water and provision', lineage: 'water-guardian', name: 'Al-Latif / Al-Muhyi' },
      { text: 'The balance and Mizan of the ecosystem', lineage: 'community-caretaker', name: 'Al-Adl / Al-Muqsit' },
      { text: 'The resilience after damage or fire', lineage: 'returning-citizen', name: 'Al-Muhyi / As-Sabur' },
    ],
  },
];

const LINEAGE_PROFILES = {
  'forest-keeper': {
    name: 'Forest Keeper Legacy',
    arabic: 'وَارِث الغَابَة',
    color: '#2d7a47',
    primaryNames: ['Al-Khaliq', 'Al-Bari', 'Al-Musawwir', 'Al-Hakim'],
    description: 'Your Fitra recognizes structure, design, and the hidden systems beneath the surface. You are drawn to understanding how things are made and why they work. The forest\'s root system is your natural metaphor.',
    irlEntry: 'Begin with S3 (Canopy Photosynthesis) or S4 (Mycorrhizal Network)',
    keysPath: 'Key 01 (Ayat) → Key 02 (Haal) → Key 03 (Taqwah)',
    mrgDomain: 'Environmental stewardship, urban forestry, ecological restoration',
  },
  'water-guardian': {
    name: 'Water Guardian Heritage',
    arabic: 'حَارِس المَاء',
    color: '#2d8a87',
    primaryNames: ['Al-Latif', 'Ar-Razzaq', 'Al-Muhyi', 'Al-Jami'],
    description: 'Your Fitra recognizes provision, flow, and connection. You are drawn to how resources move through systems and how people are nourished. The wetland\'s filtration is your natural metaphor.',
    irlEntry: 'Begin with S2 (Wetland Filtration) or S6 (Salmon Run)',
    keysPath: 'Key 05 (Amanah) → Key 04 (Mizan) → Key 03 (Taqwah)',
    mrgDomain: 'Food access, water quality, community provision networks',
  },
  'community-caretaker': {
    name: 'Community Caretaker Path',
    arabic: 'خَادِم المُجْتَمَع',
    color: '#c9a84c',
    primaryNames: ['Al-Adl', 'Al-Muqsit', 'Al-Hafiz', 'Al-Wakil'],
    description: 'Your Fitra recognizes balance, justice, and the needs of others. You are drawn to who is served and who is excluded. The Mizan — the balance of the ecosystem — is your natural metaphor.',
    irlEntry: 'Begin with S1 (Big Bluestem) or S2 (Wetland Filtration)',
    keysPath: 'Key 04 (Mizan) → Key 05 (Amanah) → Key 06 (Falah)',
    mrgDomain: 'Housing stability, health equity, community organizing',
  },
  'returning-citizen': {
    name: 'Returning Citizen Lineage',
    arabic: 'المُعَاد إِلَى المُجْتَمَع',
    color: '#a07840',
    primaryNames: ['At-Tawwab', 'Al-Jabbar', 'As-Sabur', 'Al-Muhyi'],
    description: 'Your Fitra recognizes resilience, restoration, and the possibility of return. You are drawn to what survives damage and what can be rebuilt. The metamorphosis — dissolution into new form — is your natural metaphor.',
    irlEntry: 'Begin with S5 (Amphibian Metamorphosis) or S7 (Longleaf Pine Grass Stage)',
    keysPath: 'Key 02 (Haal) → Key 03 (Taqwah) → Key 05 (Amanah)',
    mrgDomain: 'Reentry support, restorative justice, community restoration',
  },
};

export function FitraDiagnostic() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<keyof typeof LINEAGE_PROFILES | null>(null);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (questionId: string, lineage: string) => {
    const newAnswers = { ...answers, [questionId]: lineage };
    setAnswers(newAnswers);
    if (step < FITRA_RECOGNITIONS.length - 1) {
      setTimeout(() => setStep(s => s + 1), 300);
    } else {
      // Tally lineage votes
      const tally: Record<string, number> = {};
      Object.values(newAnswers).forEach(l => { tally[l] = (tally[l] || 0) + 1; });
      const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0] as keyof typeof LINEAGE_PROFILES;
      setResult(top);
      setTimeout(() => setShowResult(true), 400);
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('haal_fitra_lineage', top);
        localStorage.setItem('haal_fitra_answers', JSON.stringify(newAnswers));
      }
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setShowResult(false);
  };

  const currentQ = FITRA_RECOGNITIONS[step];
  const profile = result ? LINEAGE_PROFILES[result] : null;

  return (
    <div style={{ margin: '40px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          ◈ FITRA DIAGNOSTIC — LINEAGE RECOGNITION
        </div>
        <h3 style={{ fontSize: 20, color: C.cream, fontWeight: 600, marginBottom: 8 }}>
          What do you already recognize?
        </h3>
        <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.75, maxWidth: 680 }}>
          "Our 'Fitrah', Original Design, upon which we were created is an indicator of free will." The garden doesn't assign your lineage — it surfaces what your Fitra already recognizes. You don't choose. You recognize.
        </p>
      </div>

      {!showResult ? (
        <div>
          {/* Progress */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {FITRA_RECOGNITIONS.map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 3,
                  background: i <= step ? C.gold : 'rgba(201,168,76,0.15)',
                  borderRadius: 2,
                  transition: 'background 0.3s',
                }}
              />
            ))}
          </div>

          {/* Question */}
          <div style={{
            background: 'rgba(6,13,18,0.8)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 10,
            padding: '24px',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 9, color: C.goldText, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 12 }}>
              Recognition {step + 1} of {FITRA_RECOGNITIONS.length}
            </div>
            <p style={{ fontSize: 15, color: C.cream, lineHeight: 1.75, marginBottom: 20, fontFamily: "'Georgia', serif" }}>
              {currentQ.prompt}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {currentQ.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleAnswer(currentQ.id, opt.lineage)}
                  style={{
                    background: answers[currentQ.id] === opt.lineage ? 'rgba(201,168,76,0.1)' : 'rgba(6,13,18,0.5)',
                    border: `1px solid ${answers[currentQ.id] === opt.lineage ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.12)'}`,
                    borderRadius: 8,
                    padding: '14px 16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 13, color: C.cream, lineHeight: 1.5 }}>{opt.text}</span>
                  <span style={{ fontSize: 9, color: C.goldText, flexShrink: 0, fontStyle: 'italic' }}>{opt.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : profile && result ? (
        <div style={{
          background: `${profile.color}08`,
          border: `1px solid ${profile.color}30`,
          borderTop: `3px solid ${profile.color}`,
          borderRadius: 10,
          padding: '24px',
          animation: 'fadeIn 0.4s ease',
        }}>
          <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <div style={{ fontSize: 8, color: profile.color, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
            ◎ YOUR FITRA RECOGNITION — LINEAGE SURFACED
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: C.gold, direction: 'rtl', marginBottom: 6 }}>
            {profile.arabic}
          </div>
          <h4 style={{ fontSize: 18, color: C.cream, fontWeight: 600, marginBottom: 12 }}>{profile.name}</h4>
          <p style={{ fontSize: 13, color: C.creamFaint, lineHeight: 1.8, fontStyle: 'italic', marginBottom: 16 }}>
            {profile.description}
          </p>

          {/* Primary Names */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 8, color: profile.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
              Names Your Fitra Already Recognizes
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {profile.primaryNames.map(name => (
                <span key={name} style={{
                  fontSize: 10,
                  color: profile.color,
                  background: `${profile.color}15`,
                  border: `1px solid ${profile.color}30`,
                  borderRadius: 4,
                  padding: '3px 8px',
                }}>
                  {name}
                </span>
              ))}
            </div>
          </div>

          {/* Paths */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div style={{ background: 'rgba(6,13,18,0.4)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6, padding: '12px' }}>
              <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>IRL Lens Entry</div>
              <p style={{ fontSize: 11, color: C.creamFaint, lineHeight: 1.6 }}>{profile.irlEntry}</p>
            </div>
            <div style={{ background: 'rgba(6,13,18,0.4)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6, padding: '12px' }}>
              <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Keys Path</div>
              <p style={{ fontSize: 11, color: C.creamFaint, lineHeight: 1.6 }}>{profile.keysPath}</p>
            </div>
          </div>

          <div style={{ background: 'rgba(6,13,18,0.4)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6, padding: '12px', marginBottom: 16 }}>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>MRG Amanah Domain</div>
            <p style={{ fontSize: 11, color: C.creamFaint, lineHeight: 1.6 }}>{profile.mrgDomain}</p>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link href="/user-dashboard" style={{
              fontSize: 10,
              color: profile.color,
              textDecoration: 'none',
              border: `1px solid ${profile.color}40`,
              borderRadius: 4,
              padding: '8px 14px',
              background: `${profile.color}10`,
              letterSpacing: '.06em',
            }}>
              → Enter My Garden with this lineage
            </Link>
            <button onClick={reset} style={{
              fontSize: 10,
              color: '#7a6f5d',
              background: 'none',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 4,
              padding: '8px 14px',
              cursor: 'pointer',
              letterSpacing: '.06em',
            }}>
              ↺ Recognize again
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. THREE LOCATIONS OF AYAT — TRIPARTITE DISPLAY
// Within / Around / Revealed with luminosity increasing when all three active
// ─────────────────────────────────────────────────────────────────────────────

export function ThreeLocationsOfAyat() {
  const [activeLocations, setActiveLocations] = useState<Set<string>>(new Set());

  const toggleLocation = (loc: string) => {
    setActiveLocations(prev => {
      const next = new Set(prev);
      if (next.has(loc)) next.delete(loc);
      else next.add(loc);
      return next;
    });
  };

  const allActive = activeLocations.size === 3;
  const luminosity = activeLocations.size / 3;

  const locations = [
    {
      id: 'within',
      label: 'Within Us',
      arabic: 'أَنْفُسِهِمْ',
      subtitle: 'Anfus — The Inner Signs',
      color: C.anfus,
      icon: '◉',
      description: 'The Fitra, the inner voice, the heart\'s capacity for recognition, the Nafs and its states. Ayat inherent within us — the compass needle that always wants to point north.',
      tool: 'Inner Lens — Three Questions applied to the self',
      verse: 'Quran 41:53 — "in themselves"',
      examples: ['The feeling of awe before beauty', 'The conscience that recognizes injustice', 'The longing for meaning', 'The recognition of gratitude'],
    },
    {
      id: 'around',
      label: 'Around Us',
      arabic: 'الْآفَاقِ',
      subtitle: 'Afaq — The Horizon Signs',
      color: C.afaq,
      icon: '◎',
      description: 'Creation, ecology, the manifest world, the environment the IRL Lens reads. Ayat in the horizons — the forest, the ward, the wetland, the sky.',
      tool: 'IRL Lens — 8-layer recognition pathway',
      verse: 'Quran 41:53 — "in the horizons"',
      examples: ['The mycorrhizal network beneath the forest', 'The wetland filtering the watershed', 'The vacant lot in the ward', 'The cherry blossoms by the waterfront'],
    },
    {
      id: 'revealed',
      label: 'Revealed to Us',
      arabic: 'الْقُرْآن',
      subtitle: 'Quran — The Revealed Signs',
      color: C.quranMode,
      icon: '◈',
      description: 'The Quran, the Sunnah, the guidance sent through Prophets and Messengers. Most critically — the criterion (Furqan) by which the other two are measured.',
      tool: 'Keys Curriculum — Sacred Language as vocabulary for both other tracks',
      verse: 'Quran 41:53 — "We will show them... until it becomes clear"',
      examples: ['Quran 6:59 — not a leaf falls but He knows it', 'Quran 15:21 — everything sent down in a known measure', 'Quran 24:35 — Allah is the Light', 'Quran 87:1-3 — created, proportioned, measured, guided'],
    },
  ];

  return (
    <div style={{ margin: '40px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          ◈ THREE LOCATIONS OF AYAT — TRIPARTITE ARCHITECTURE
        </div>
        <h3 style={{ fontSize: 20, color: C.cream, fontWeight: 600, marginBottom: 8 }}>
          Within · Around · Revealed
        </h3>
        <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.75, maxWidth: 680, marginBottom: 8 }}>
          "Some of the Ayat, Signs are inherent within us, some are in nature around us, and most critically, some are Revealed to us." Activate all three to reach full luminosity.
        </p>
        {allActive && (
          <div style={{
            fontSize: 11,
            color: C.gold,
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 6,
            padding: '8px 14px',
            display: 'inline-block',
            animation: 'fadeIn 0.4s ease',
          }}>
            ✦ Full luminosity — all three Ayat tracks active. This is the garden's fullest state.
          </div>
        )}
      </div>

      {/* Luminosity indicator */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 8, color: '#4a4035', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
          Garden Luminosity
        </div>
        <div style={{ height: 6, background: 'rgba(201,168,76,0.1)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${luminosity * 100}%`,
            background: `linear-gradient(90deg, ${C.anfus}, ${C.afaq}, ${C.quranMode})`,
            borderRadius: 3,
            transition: 'width 0.4s ease',
            boxShadow: allActive ? '0 0 12px rgba(201,168,76,0.4)' : 'none',
          }} />
        </div>
        <div style={{ fontSize: 9, color: '#4a4035', marginTop: 4 }}>
          {activeLocations.size}/3 tracks active
        </div>
      </div>

      {/* Three hemispheres */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
        {locations.map(loc => {
          const isActive = activeLocations.has(loc.id);
          return (
            <div
              key={loc.id}
              onClick={() => toggleLocation(loc.id)}
              style={{
                background: isActive ? `${loc.color}10` : 'rgba(6,13,18,0.6)',
                border: `1px solid ${isActive ? loc.color + '50' : 'rgba(201,168,76,0.12)'}`,
                borderTop: `3px solid ${isActive ? loc.color : 'rgba(201,168,76,0.15)'}`,
                borderRadius: 10,
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.25s',
                opacity: isActive ? 1 : 0.7,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 20, color: isActive ? loc.color : '#4a4035' }}>{loc.icon}</span>
                <div style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: isActive ? loc.color : 'rgba(201,168,76,0.15)',
                  boxShadow: isActive ? `0 0 8px ${loc.color}` : 'none',
                  transition: 'all 0.3s',
                }} />
              </div>
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: isActive ? C.gold : '#4a4035', direction: 'rtl', marginBottom: 4 }}>
                {loc.arabic}
              </div>
              <div style={{ fontSize: 13, color: isActive ? C.cream : '#7a6f5d', fontWeight: 500, marginBottom: 4 }}>{loc.label}</div>
              <div style={{ fontSize: 9, color: isActive ? loc.color : '#4a4035', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
                {loc.subtitle}
              </div>
              <p style={{ fontSize: 11, color: isActive ? C.creamFaint : '#4a4035', lineHeight: 1.65, marginBottom: 12 }}>
                {loc.description}
              </p>
              {isActive && (
                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                  <div style={{ fontSize: 9, color: loc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Tool
                  </div>
                  <p style={{ fontSize: 10, color: C.creamFaint, marginBottom: 10 }}>{loc.tool}</p>
                  <div style={{ fontSize: 9, color: loc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Quranic Anchor
                  </div>
                  <p style={{ fontSize: 10, color: C.creamFaint, marginBottom: 10, fontStyle: 'italic' }}>{loc.verse}</p>
                  <div style={{ fontSize: 9, color: loc.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Examples
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 14px' }}>
                    {loc.examples.map((ex, i) => (
                      <li key={i} style={{ fontSize: 10, color: C.creamFaint, lineHeight: 1.6 }}>{ex}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Root connection */}
      {allActive && (
        <div style={{
          marginTop: 20,
          background: 'rgba(201,168,76,0.05)',
          border: '1px solid rgba(201,168,76,0.25)',
          borderRadius: 8,
          padding: '16px 20px',
          animation: 'fadeIn 0.5s ease',
        }}>
          <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            ◎ ROOT CONNECTION — MA'RIFAH
          </div>
          <p style={{ fontSize: 13, color: C.cream, lineHeight: 1.8, fontStyle: 'italic' }}>
            "Soon, We will show them Our Ayat in the horizons and in themselves until becomes clear to them that it's the truth/right/reality/purpose." — Quran 41:53. The three tracks are not separate — they are the same reality perceived from three angles. The leaf is Ayat around you. The verse about the leaf is Ayat Revealed to you. The awe you feel is Ayat within you. One reality. Three registers. One garden.
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. FIXED AL-QADIR PULL QUOTE
// Replace paraphrase with direct letter text
// ─────────────────────────────────────────────────────────────────────────────

export function AlQadirPullQuote() {
  return (
    <div style={{
      background: 'rgba(201,168,76,0.04)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderLeft: '4px solid rgba(201,168,76,0.6)',
      borderRadius: '0 10px 10px 0',
      padding: '20px 24px',
      margin: '24px 0',
    }}>
      <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
        ◈ AL-QADIR — DIRECT LETTER TEXT
      </div>
      <blockquote style={{
        fontFamily: "'Georgia', serif",
        fontSize: 16,
        color: C.cream,
        lineHeight: 1.85,
        fontStyle: 'italic',
        margin: 0,
        marginBottom: 10,
      }}>
        "Every measurement given to a thing is prescribed by the owner of the highest embodiment of that concept."
      </blockquote>
      <div style={{ fontSize: 10, color: C.goldText }}>
        — Open Letter, Al-Qadir section · Discover and explore the Taqdir, measurements given to creation
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. MONTHLY SEED SUMMARY
// "These seeds are planted. The harvest belongs to Allah."
// ─────────────────────────────────────────────────────────────────────────────

export function MonthlySeedSummary() {
  const [expanded, setExpanded] = useState(false);

  // Mock data — in production this would come from Supabase
  const seedData = {
    month: 'This Month',
    ayatRecognized: 4,
    resourcesBridged: 2,
    observationsSubmitted: 1,
    niyyahsSet: 3,
    recoveriesFromNeglect: 2,
  };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.85)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
      >
        <div>
          <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 4 }}>
            ◈ MONTHLY SEED SUMMARY
          </div>
          <div style={{ fontSize: 14, color: C.cream }}>Seeds Planted — {seedData.month}</div>
        </div>
        <span style={{ color: C.gold, fontSize: 14 }}>{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 20px 20px', animation: 'fadeIn 0.25s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Ayat Recognized', value: seedData.ayatRecognized, color: C.green },
              { label: 'Resources Bridged', value: seedData.resourcesBridged, color: C.teal },
              { label: 'Observations Submitted', value: seedData.observationsSubmitted, color: C.amber },
              { label: "Niyyah's Set", value: seedData.niyyahsSet, color: C.gold },
              { label: 'Recoveries from Neglect', value: seedData.recoveriesFromNeglect, color: C.purple },
            ].map(item => (
              <div key={item.label} style={{
                background: `${item.color}08`,
                border: `1px solid ${item.color}20`,
                borderRadius: 6,
                padding: '12px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 24, color: item.color, fontWeight: 700, marginBottom: 4 }}>{item.value}</div>
                <div style={{ fontSize: 9, color: '#7a6f5d', lineHeight: 1.4 }}>{item.label}</div>
              </div>
            ))}
          </div>

          <div style={{
            background: 'rgba(201,168,76,0.05)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: '14px 16px',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: 13, color: C.cream, lineHeight: 1.8, fontStyle: 'italic', marginBottom: 6 }}>
              "These seeds are planted. The harvest belongs to Allah."
            </p>
            <p style={{ fontSize: 10, color: C.goldText }}>
              "We were given tools/provisions, examples, and instructions — told to do as best we can, reminded that we'll be returning to our Creator to receive what our efforts planted."
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. COMPOST RECOVERY COUNTER
// Counts recoveries from neglected modules alongside recognitions
// ─────────────────────────────────────────────────────────────────────────────

export function CompostRecoveryCounter() {
  const [recognitions, setRecognitions] = useState(7);
  const [recoveries, setRecoveries] = useState(3);
  const [showAdd, setShowAdd] = useState<'recognition' | 'recovery' | null>(null);
  const [note, setNote] = useState('');

  const handleAdd = (type: 'recognition' | 'recovery') => {
    if (type === 'recognition') setRecognitions(v => v + 1);
    else setRecoveries(v => v + 1);
    setNote('');
    setShowAdd(null);
  };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.85)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10,
      padding: '20px',
      margin: '24px 0',
    }}>
      <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 12 }}>
        ◈ ALHAMDULILLAH — RECOGNITIONS & RECOVERIES
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {/* Recognitions */}
        <div style={{
          background: 'rgba(45,122,71,0.08)',
          border: '1px solid rgba(45,122,71,0.25)',
          borderRadius: 8,
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, color: C.green, fontWeight: 700, marginBottom: 4 }}>{recognitions}</div>
          <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            Recognitions
          </div>
          <button
            onClick={() => setShowAdd('recognition')}
            style={{
              background: 'rgba(45,122,71,0.12)',
              border: '1px solid rgba(45,122,71,0.3)',
              color: C.green,
              fontSize: 9,
              padding: '4px 10px',
              cursor: 'pointer',
              borderRadius: 4,
            }}
          >
            + Add
          </button>
        </div>

        {/* Recoveries */}
        <div style={{
          background: 'rgba(122,74,138,0.08)',
          border: '1px solid rgba(122,74,138,0.25)',
          borderRadius: 8,
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, color: C.purple, fontWeight: 700, marginBottom: 4 }}>{recoveries}</div>
          <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            Recoveries
          </div>
          <button
            onClick={() => setShowAdd('recovery')}
            style={{
              background: 'rgba(122,74,138,0.12)',
              border: '1px solid rgba(122,74,138,0.3)',
              color: C.purple,
              fontSize: 9,
              padding: '4px 10px',
              cursor: 'pointer',
              borderRadius: 4,
            }}
          >
            + Add
          </button>
        </div>
      </div>

      {showAdd && (
        <div style={{
          background: 'rgba(6,13,18,0.6)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 8,
          padding: '14px',
          marginBottom: 12,
          animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{ fontSize: 9, color: C.goldText, marginBottom: 8 }}>
            {showAdd === 'recognition' ? 'What did you recognize?' : 'What did you return to after neglect?'}
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={showAdd === 'recognition' ? 'I recognized...' : 'I returned to...'}
            style={{
              width: '100%',
              minHeight: 60,
              background: 'rgba(6,13,18,0.5)',
              border: '1px solid rgba(201,168,76,0.12)',
              borderRadius: 4,
              color: C.cream,
              fontSize: 12,
              padding: '8px 10px',
              resize: 'vertical',
              fontFamily: "'Georgia', serif",
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 8,
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleAdd(showAdd)}
              style={{
                background: showAdd === 'recognition' ? 'rgba(45,122,71,0.15)' : 'rgba(122,74,138,0.15)',
                border: `1px solid ${showAdd === 'recognition' ? 'rgba(45,122,71,0.4)' : 'rgba(122,74,138,0.4)'}`,
                color: showAdd === 'recognition' ? C.green : C.purple,
                fontSize: 10,
                padding: '6px 14px',
                cursor: 'pointer',
                borderRadius: 4,
              }}
            >
              الحمد لله — Record
            </button>
            <button
              onClick={() => setShowAdd(null)}
              style={{ background: 'none', border: '1px solid rgba(201,168,76,0.12)', color: '#7a6f5d', fontSize: 10, padding: '6px 10px', cursor: 'pointer', borderRadius: 4 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.12)',
        borderRadius: 6,
        padding: '10px 12px',
      }}>
        <p style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.65, fontStyle: 'italic' }}>
          "To slip and grow and struggle, to put in effort and strive in the Deen... Perfection is neither expected nor what we're made for." The garden rewards persistence, not perfection. Recoveries count.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. 41:53 ARRIVAL VERSE — FOUR HAQQ DIMENSIONS
// Placed as culmination of ROOTS Ma'rifah, not as premise
// ─────────────────────────────────────────────────────────────────────────────

export function Verse4153ArrivalBlock() {
  const [activeHaqq, setActiveHaqq] = useState<number | null>(null);
  const [arrived, setArrived] = useState(false);

  const haqqDimensions = [
    {
      dimension: 'Truth',
      arabic: 'الحَقّ — صِدْق',
      color: C.green,
      description: 'Factually accurate. The signs in the horizons and in themselves are real — not metaphor, not symbol, but actual evidence of actual reality. The leaf is a real leaf. The Ayah is a real sign.',
      irlLayer: 'Layer 1 (Encounter/Zahir) — the thing is real, it exists, it is perceived',
    },
    {
      dimension: 'Right',
      arabic: 'الحَقّ — صَوَاب',
      color: C.teal,
      description: 'Morally correct. The recognition of the signs is not just intellectually valid — it is the right orientation. To see the signs and acknowledge the Source is the morally correct response to creation.',
      irlLayer: 'Layer 7 (Amanah) — the recognition calls to right action',
    },
    {
      dimension: 'Reality',
      arabic: 'الحَقّ — وَاقِع',
      color: C.amber,
      description: 'Ontologically actual. The Haqq is not an interpretation — it is the substrate. Creation was made "in Haqq" (Quran 15:85). Reality is the medium, not the byproduct.',
      irlLayer: 'Layer 6 (Haqq) — the truth of what this thing actually is',
    },
    {
      dimension: 'Purpose',
      arabic: 'الحَقّ — غَرَض',
      color: C.purple,
      description: 'Teleologically meaningful. The signs point toward a purpose — not random, not accidental. "Until it becomes clear to them" — the clarity is the purpose. Recognition is the destination.',
      irlLayer: 'Layer 8 (Uboor/Crossing) — the recognition completes in return to the Source',
    },
  ];

  return (
    <div style={{ margin: '40px 0' }}>
      {/* Arrival framing */}
      {!arrived ? (
        <div style={{
          background: 'rgba(6,13,18,0.9)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 10,
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ ROOTS MA'RIFAH — CULMINATION
          </div>
          <p style={{ fontSize: 13, color: C.creamFaint, lineHeight: 1.8, maxWidth: 560, margin: '0 auto 20px', fontStyle: 'italic' }}>
            Everything described in ROOTS Ma'rifah — the two types of Ayat, the Fitra as original design, willing submission as practice, imperfection as design, three locations of signs, a reminder to work — arrives here. This verse is not a premise. It is an arrival.
          </p>
          <button
            onClick={() => setArrived(true)}
            style={{
              background: 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.4)',
              color: C.gold,
              fontSize: 11,
              padding: '10px 24px',
              cursor: 'pointer',
              borderRadius: 6,
              letterSpacing: '.1em',
            }}
          >
            Arrive at the verse →
          </button>
        </div>
      ) : (
        <div style={{ animation: 'fadeIn 0.6s ease' }}>
          {/* The verse */}
          <div style={{
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 12,
            padding: '32px',
            marginBottom: 24,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 16 }}>
              Quran 41:53
            </div>
            <div style={{
              fontFamily: "'Amiri', serif",
              fontSize: 22,
              color: C.gold,
              direction: 'rtl',
              lineHeight: 1.8,
              marginBottom: 16,
            }}>
              سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ حَتَّىٰ يَتَبَيَّنَ لَهُمْ أَنَّهُ الْحَقُّ
            </div>
            <p style={{ fontSize: 15, color: C.cream, lineHeight: 1.85, fontStyle: 'italic', maxWidth: 600, margin: '0 auto 12px' }}>
              "Soon, We will show them Our Ayat in the horizons and in themselves until becomes clear to them that it's the truth/right/reality/purpose."
            </p>
            <div style={{ fontSize: 10, color: C.goldText }}>
              Al-Haqq — four dimensions simultaneously: truth · right · reality · purpose
            </div>
          </div>

          {/* Four Haqq dimensions */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 12 }}>
              The Four Dimensions of Al-Haqq
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {haqqDimensions.map((dim, i) => (
                <div
                  key={i}
                  onClick={() => setActiveHaqq(activeHaqq === i ? null : i)}
                  style={{
                    background: activeHaqq === i ? `${dim.color}10` : 'rgba(6,13,18,0.6)',
                    border: `1px solid ${activeHaqq === i ? dim.color + '40' : 'rgba(201,168,76,0.12)'}`,
                    borderTop: `3px solid ${dim.color}`,
                    borderRadius: 8,
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: dim.color, direction: 'rtl', marginBottom: 6 }}>
                    {dim.arabic}
                  </div>
                  <div style={{ fontSize: 13, color: C.cream, fontWeight: 500, marginBottom: 8 }}>{dim.dimension}</div>
                  {activeHaqq === i && (
                    <div style={{ animation: 'fadeIn 0.2s ease' }}>
                      <p style={{ fontSize: 11, color: C.creamFaint, lineHeight: 1.65, marginBottom: 8 }}>{dim.description}</p>
                      <div style={{ fontSize: 9, color: dim.color, fontStyle: 'italic' }}>{dim.irlLayer}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* The promise */}
          <div style={{
            background: 'rgba(201,168,76,0.05)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: '16px 20px',
          }}>
            <p style={{ fontSize: 12, color: C.creamFaint, lineHeight: 1.8, fontStyle: 'italic' }}>
              "Soon, We will show them." Not "we might." "We will." The signs will become clear. The platform exists in the gap between the promise and its fulfillment. The garden provides Ilm and Ayn Yaqeen. Haqq Yaqeen — the full clarity of Al-Haqq — is His to grant.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. "A REMINDER TO WORK" PROMPT
// Surfaces after any session longer than 20 minutes
// ─────────────────────────────────────────────────────────────────────────────

export function ReminderToWork() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const sessionStartRef = useRef<number>(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = (Date.now() - sessionStartRef.current) / 1000 / 60;
      if (elapsed >= 20 && !dismissed) {
        setVisible(true);
      }
    }, 60000); // check every minute

    // For demo: show after 3 seconds
    const demo = setTimeout(() => {
      const stored = sessionStorage.getItem('haal_reminder_dismissed');
      if (!stored) setVisible(true);
    }, 3000);

    return () => { clearInterval(interval); clearTimeout(demo); };
  }, [dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem('haal_reminder_dismissed', '1');
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      left: 24,
      zIndex: 1000,
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(201,168,76,0.25)',
      borderRadius: 8,
      padding: '12px 16px',
      maxWidth: 260,
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      animation: 'slideUp 0.4s ease',
    }}>
      <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 7, color: C.goldText, letterSpacing: '.15em', textTransform: 'uppercase' }}>
          ◈ A REMINDER TO WORK
        </div>
        <button
          onClick={handleDismiss}
          style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.3)', fontSize: 12, cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
      <p style={{ fontSize: 11, color: C.creamFaint, lineHeight: 1.6, marginBottom: 8 }}>
        After all the architecture — go work. Plant something. Serve someone. Bridge a resource.
      </p>
      <Link
        href="/inner-reality-lens"
        onClick={handleDismiss}
        style={{
          display: 'block',
          textAlign: 'center',
          fontSize: 9,
          color: C.gold,
          textDecoration: 'none',
          border: '1px solid rgba(201,168,76,0.3)',
          borderRadius: 4,
          padding: '5px 10px',
          background: 'rgba(201,168,76,0.06)',
          letterSpacing: '.06em',
        }}
      >
        → Go observe
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ENHANCED AMANAH BRIDGE SYSTEM WRAPPER
// Adds Yaqeen-level tagging, role-context filtering, MRG pre-matching
// ─────────────────────────────────────────────────────────────────────────────

const CITY_WARD_MRG_MAP: Record<string, Record<string, string[]>> = {
  chicago: {
    'south-side': ['food-access', 'housing', 'reentry', 'economic'],
    'west-side': ['food-access', 'health', 'education', 'economic'],
    'north-side': ['housing', 'economic'],
    'roseland': ['food-access', 'housing', 'reentry', 'health'],
    'englewood': ['food-access', 'housing', 'economic', 'health'],
  },
  atlanta: {
    'southwest': ['food-access', 'housing', 'reentry'],
    'southeast': ['food-access', 'health', 'education'],
    'downtown': ['housing', 'economic'],
  },
  detroit: {
    'east-side': ['food-access', 'housing', 'economic', 'reentry'],
    'west-side': ['food-access', 'health', 'education'],
  },
};

const ROLE_RESOURCE_PRIORITY: Record<string, string[]> = {
  engineer: ['economic', 'education'],
  teacher: ['education', 'health'],
  scientist: ['health', 'education'],
  farmer: ['food-access', 'economic'],
  native: ['housing', 'economic', 'food-access'],
  'returning-citizen': ['reentry', 'housing', 'economic'],
  student: ['education', 'economic'],
  'warehouse-worker': ['economic', 'health'],
  janitor: ['health', 'housing'],
  'law-student': ['reentry', 'housing', 'economic'],
};

const YAQEEN_RESOURCE_TAGS: Record<string, { level: 'ilm' | 'ayn' | 'haqq'; label: string; color: string }> = {
  'food-access': { level: 'haqq', label: 'Haqq Yaqeen — walk to the food pantry', color: C.green },
  'housing': { level: 'ayn', label: 'Ayn Yaqeen — see the disparity data', color: C.amber },
  'education': { level: 'ilm', label: 'Ilm Yaqeen — study the resource', color: C.teal },
  'health': { level: 'ayn', label: 'Ayn Yaqeen — witness the health gap', color: C.purple },
  'reentry': { level: 'haqq', label: 'Haqq Yaqeen — meet the person', color: C.amber },
  'economic': { level: 'ayn', label: 'Ayn Yaqeen — see the economic data', color: C.teal },
};

export function EnhancedAmanahBridgeFilter() {
  const [city, setCity] = useState('chicago');
  const [ward, setWard] = useState('south-side');
  const [role, setRole] = useState('returning-citizen');
  const [yaqeenFilter, setYaqeenFilter] = useState<'all' | 'ilm' | 'ayn' | 'haqq'>('all');

  const cityWards = CITY_WARD_MRG_MAP[city] || {};
  const wardNeeds = cityWards[ward] || [];
  const roleNeeds = ROLE_RESOURCE_PRIORITY[role] || [];

  // Pre-match: intersection of ward needs and role needs, then remaining ward needs
  const prioritized = wardNeeds.filter(n => roleNeeds.includes(n));
  const secondary = wardNeeds.filter(n => !roleNeeds.includes(n));
  const allNeeds = [...prioritized, ...secondary];

  const filtered = yaqeenFilter === 'all'
    ? allNeeds
    : allNeeds.filter(n => YAQEEN_RESOURCE_TAGS[n]?.level === yaqeenFilter);

  const yaqeenColors = { ilm: C.teal, ayn: C.amber, haqq: C.green };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.85)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      {/* Header */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid rgba(201,168,76,0.1)', background: 'rgba(201,168,76,0.03)' }}>
        <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◈ MRG PRE-MATCH — CITY / WARD / ROLE
        </div>
        <div style={{ fontSize: 15, color: C.cream, marginBottom: 4 }}>
          Amanah Bridge System — Enhanced
        </div>
        <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6 }}>
          Resources pre-matched by your city, ward, and role. Yaqeen-tagged so you know what level of certainty each resource requires.
        </p>
      </div>

      {/* Filters */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {/* City */}
          <div>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>City</div>
            <select
              value={city}
              onChange={e => { setCity(e.target.value); setWard(Object.keys(CITY_WARD_MRG_MAP[e.target.value] || {})[0] || ''); }}
              style={{
                width: '100%',
                background: 'rgba(6,13,18,0.6)',
                border: '1px solid rgba(201,168,76,0.2)',
                color: C.cream,
                fontSize: 11,
                padding: '6px 10px',
                borderRadius: 4,
                outline: 'none',
              }}
            >
              {Object.keys(CITY_WARD_MRG_MAP).map(c => (
                <option key={c} value={c} style={{ background: '#060d12' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Ward */}
          <div>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Ward</div>
            <select
              value={ward}
              onChange={e => setWard(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(6,13,18,0.6)',
                border: '1px solid rgba(201,168,76,0.2)',
                color: C.cream,
                fontSize: 11,
                padding: '6px 10px',
                borderRadius: 4,
                outline: 'none',
              }}
            >
              {Object.keys(cityWards).map(w => (
                <option key={w} value={w} style={{ background: '#060d12' }}>{w.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')}</option>
              ))}
            </select>
          </div>

          {/* Role */}
          <div>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Your Role</div>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(6,13,18,0.6)',
                border: '1px solid rgba(201,168,76,0.2)',
                color: C.cream,
                fontSize: 11,
                padding: '6px 10px',
                borderRadius: 4,
                outline: 'none',
              }}
            >
              {Object.keys(ROLE_RESOURCE_PRIORITY).map(r => (
                <option key={r} value={r} style={{ background: '#060d12' }}>{r.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')}</option>
              ))}
            </select>
          </div>

          {/* Yaqeen filter */}
          <div>
            <div style={{ fontSize: 8, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Yaqeen Level</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['all', 'ilm', 'ayn', 'haqq'] as const).map(level => (
                <button
                  key={level}
                  onClick={() => setYaqeenFilter(level)}
                  style={{
                    flex: 1,
                    background: yaqeenFilter === level ? `${level === 'all' ? C.gold : yaqeenColors[level as keyof typeof yaqeenColors]}20` : 'transparent',
                    border: `1px solid ${yaqeenFilter === level ? (level === 'all' ? C.gold : yaqeenColors[level as keyof typeof yaqeenColors]) : 'rgba(201,168,76,0.12)'}`,
                    color: yaqeenFilter === level ? (level === 'all' ? C.gold : yaqeenColors[level as keyof typeof yaqeenColors]) : '#7a6f5d',
                    fontSize: 8,
                    padding: '4px 2px',
                    cursor: 'pointer',
                    borderRadius: 3,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pre-matched resources */}
      <div style={{ padding: '16px 20px' }}>
        {filtered.length === 0 ? (
          <p style={{ fontSize: 12, color: '#4a4035', fontStyle: 'italic' }}>No resources match this filter combination.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map((needId, i) => {
              const isPriority = prioritized.includes(needId);
              const yaqeen = YAQEEN_RESOURCE_TAGS[needId];
              const needColors: Record<string, string> = {
                'food-access': C.green, 'housing': C.amber, 'education': C.teal,
                'health': C.purple, 'reentry': '#c9a84c', 'economic': '#5a8a5a',
              };
              const needLabels: Record<string, string> = {
                'food-access': 'Food Access', 'housing': 'Housing Stability', 'education': 'Education Access',
                'health': 'Health & Wellness', 'reentry': 'Reentry Support', 'economic': 'Economic Opportunity',
              };
              const needNames: Record<string, string> = {
                'food-access': 'Ar-Razzaq', 'housing': 'Al-Hafiz', 'education': 'Al-Alim',
                'health': 'Ash-Shafi', 'reentry': 'At-Tawwab', 'economic': 'Ar-Razzaq',
              };
              const color = needColors[needId] || C.gold;

              return (
                <div key={needId} style={{
                  background: `${color}08`,
                  border: `1px solid ${color}25`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: '0 8px 8px 0',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isPriority && (
                        <span style={{ fontSize: 8, color: color, background: `${color}15`, border: `1px solid ${color}30`, borderRadius: 3, padding: '2px 6px', letterSpacing: '.08em' }}>
                          ROLE MATCH
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: C.cream, fontWeight: 500 }}>{needLabels[needId]}</span>
                      <span style={{ fontSize: 10, color: color, fontStyle: 'italic' }}>{needNames[needId]}</span>
                    </div>
                    {yaqeen && (
                      <span style={{
                        fontSize: 8,
                        color: yaqeenColors[yaqeen.level as keyof typeof yaqeenColors],
                        background: `${yaqeenColors[yaqeen.level as keyof typeof yaqeenColors]}15`,
                        border: `1px solid ${yaqeenColors[yaqeen.level as keyof typeof yaqeenColors]}30`,
                        borderRadius: 3,
                        padding: '2px 6px',
                        letterSpacing: '.06em',
                        textTransform: 'uppercase',
                      }}>
                        {yaqeen.level} yaqeen
                      </span>
                    )}
                  </div>
                  {yaqeen && (
                    <p style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.6, marginBottom: 8, fontStyle: 'italic' }}>
                      {yaqeen.label}
                    </p>
                  )}
                  <Link
                    href={`/muslim-resource-guide?type=${needId}&city=${city}&ward=${ward}&role=${role}`}
                    style={{
                      fontSize: 9,
                      color: color,
                      textDecoration: 'none',
                      border: `1px solid ${color}30`,
                      borderRadius: 3,
                      padding: '4px 10px',
                      letterSpacing: '.06em',
                    }}
                  >
                    → Bridge this Amanah in MRG
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT — PLOT 4 ENHANCEMENTS SECTION
// ─────────────────────────────────────────────────────────────────────────────

export default function Plot4Enhancements() {
  return (
    <section id="section-plot4-enhancements" style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">

        {/* Section header */}
        <div style={{ marginBottom: 48 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(139,105,20,0.6)', marginBottom: 12 }}>
            ◈ Plot 04 · Garden Architecture Enhancements
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.02em', color: '#F5F0E8', marginBottom: 12 }}>
            The Garden's Full Architecture
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.75, maxWidth: 640 }}>
            Two Eyes. Three Modes. Three Locations. One Fitra. The garden's complete recognition system — built from the ROOTS Ma'rifah section of the Open Letter.
          </p>
        </div>

        {/* 1. Enhanced Amanah Bridge Filter */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 01 — Amanah Bridge System Enhanced
          </div>
          <EnhancedAmanahBridgeFilter />
        </div>

        {/* 2. Two-Eye Binocular System */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 02 — Two-Eye Binocular System
          </div>
          <TwoEyeBinocularSystem />
        </div>

        {/* 3. Three-Mode Observation Panel */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 03 — Three-Mode Observation Panel
          </div>
          <ThreeModeObservationPanel />
        </div>

        {/* 4. Fitra Diagnostic */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 04 — Fitra Diagnostic
          </div>
          <FitraDiagnostic />
        </div>

        {/* 5. Three Locations of Ayat */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 05 — Three Locations of Ayat
          </div>
          <ThreeLocationsOfAyat />
        </div>

        {/* 6. Al-Qadir Pull Quote (fixed) */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 06 — Al-Qadir Direct Text
          </div>
          <AlQadirPullQuote />
        </div>

        {/* 7. Monthly Seed Summary + Compost Recovery Counter */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 07 — Monthly Seed Summary & Compost Recovery Counter
          </div>
          <MonthlySeedSummary />
          <CompostRecoveryCounter />
        </div>

        {/* 8. 41:53 Arrival Verse */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 16 }}>
            ◈ 08 — Quran 41:53 — Arrival Verse
          </div>
          <Verse4153ArrivalBlock />
        </div>

      </div>

      {/* Reminder to Work — floating */}
      <ReminderToWork />
    </section>
  );
}
